import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, Sparkles } from "lucide-react";

export default function JoinBookforge({ variant = "primary", className = "", label = "I'm interested in Bookforge" }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setName("");
    setEmail("");
    setPhone("");
    setNote("");
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { name, email, phone, note });
      setDone(true);
      toast.success(data.message || "Thanks — our team will contact you soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const triggerStyle =
    variant === "primary"
      ? { background: "var(--bf-primary)" }
      : variant === "accent"
      ? { background: "var(--bf-accent)" }
      : {};

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setTimeout(reset, 200);
      }}
    >
      <DialogTrigger asChild>
        <Button
          className={`text-sm text-white ${className}`}
          style={triggerStyle}
          data-testid="join-bookforge-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="join-bookforge-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-3xl">
                Join Bookforge
              </DialogTitle>
              <DialogDescription>
                Leave your email and phone — our team will reach out to walk you through the platform and get you set up.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="join-bookforge-form">
              <div className="space-y-1.5">
                <Label htmlFor="join-name">Your name (optional)</Label>
                <Input
                  id="join-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Author"
                  data-testid="join-bookforge-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="join-email">Email <span style={{ color: "var(--bf-primary)" }}>*</span></Label>
                <Input
                  id="join-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="join-bookforge-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="join-phone">Phone number</Label>
                <Input
                  id="join-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                  data-testid="join-bookforge-phone"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="join-note">Anything you'd like us to know? (optional)</Label>
                <Textarea
                  id="join-note"
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="I'm writing a memoir and need help with…"
                  data-testid="join-bookforge-note"
                />
              </div>
              <DialogFooter>
                <Button
                  type="submit"
                  disabled={busy}
                  className="text-white w-full sm:w-auto"
                  style={{ background: "var(--bf-primary)" }}
                  data-testid="join-bookforge-submit"
                >
                  {busy ? "Sending…" : "Send my details"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <div className="py-6 text-center space-y-4" data-testid="join-bookforge-success">
            <div
              className="w-14 h-14 rounded-full mx-auto flex items-center justify-center"
              style={{ background: "var(--bf-surface)", border: "1px solid var(--bf-border)" }}
            >
              <Check className="w-7 h-7" style={{ color: "var(--bf-primary)" }} />
            </div>
            <h3 className="font-serif text-3xl">You're on the list</h3>
            <p className="text-sm" style={{ color: "var(--bf-text-muted)" }}>
              Our team will contact you soon at <strong>{email}</strong>.
            </p>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="join-bookforge-close"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
